import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
  ScrollArea,
} from "@sfc/ui";
import {
  BugIcon,
  CheckIcon,
  CopyIcon,
  TerminalIcon,
  Trash2Icon,
} from "lucide-react";
import { useMemo, useState, useSyncExternalStore } from "react";
import { copyTextToClipboard } from "../../utils/clipboard";
import {
  devDebugLog,
  devDebugStore,
  formatDebugEntries,
  type DebugLogEntry,
  type DebugLogLevel,
} from "../../utils/devDebug";

const levelClass: Record<DebugLogLevel, string> = {
  debug: "text-text-muted",
  info: "text-info",
  warn: "text-warning",
  error: "text-destructive",
};

function DebugEntryRow({ entry }: { entry: DebugLogEntry }) {
  const wallTime = new Date(entry.createdAt).toISOString().slice(11, 23);

  return (
    <li className="border-b border-border-subtle px-3 py-2 last:border-b-0">
      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1 font-mono text-xs">
        <span className="text-text-muted">{wallTime}</span>
        <span className={levelClass[entry.level]}>{entry.level}</span>
        <span className="text-text-secondary">[{entry.category}]</span>
        {entry.traceId != null ? (
          <span className="text-info">
            {entry.traceId} +{entry.traceElapsedMs?.toFixed(1) ?? "0.0"}ms
          </span>
        ) : null}
      </div>
      <p className="mt-1 font-mono text-xs text-text-primary break-words">
        {entry.message}
      </p>
      {entry.details != null ? (
        <pre className="mt-1 overflow-x-auto whitespace-pre-wrap break-words font-mono text-xs text-text-secondary">
          {entry.details}
        </pre>
      ) : null}
    </li>
  );
}

export function DevDebugPanel() {
  const entries = useSyncExternalStore(
    devDebugStore.subscribe,
    devDebugStore.getSnapshot,
  );
  const [copied, setCopied] = useState(false);
  const newestFirst = useMemo(() => [...entries].reverse(), [entries]);

  if (!import.meta.env.DEV) return null;

  const copyLogs = async () => {
    // The iPad dev flow serves the app over plain HTTP, where the secure-context
    // Clipboard API is absent — copyTextToClipboard falls back to execCommand.
    const copied = await copyTextToClipboard(formatDebugEntries(entries));
    if (copied) {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1_500);
    } else {
      devDebugLog("debug-ui", "Could not copy debug log", undefined, "error");
    }
  };

  return (
    <Dialog>
      <DialogTrigger
        render={
          <Button
            variant="secondary"
            size="icon-lg"
            aria-label={
              entries.length > 0
                ? `Debug log (${entries.length} events)`
                : "Debug log"
            }
            className="fixed right-4 bottom-[calc(env(safe-area-inset-bottom)+1rem)] z-40 size-12 rounded-full shadow-lg"
          />
        }
      >
        <BugIcon />
        {entries.length > 0 ? (
          <span className="absolute top-0 right-0 size-2.5 rounded-full bg-info ring-2 ring-surface-canvas" />
        ) : null}
      </DialogTrigger>
      <DialogContent className="grid h-[min(46rem,calc(100dvh-2rem))] grid-rows-[auto_auto_minmax(0,1fr)] overflow-hidden sm:max-w-4xl">
        <DialogHeader>
          <DialogTitle>Debug log</DialogTitle>
          <DialogDescription>
            Development build only. Newest events appear first.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-wrap items-center gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => void copyLogs()}
            disabled={entries.length === 0}
          >
            {copied ? (
              <CheckIcon data-icon="inline-start" />
            ) : (
              <CopyIcon data-icon="inline-start" />
            )}
            {copied ? "Copied" : "Copy log"}
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={devDebugStore.clear}
            disabled={entries.length === 0}
          >
            <Trash2Icon data-icon="inline-start" />
            Clear
          </Button>
          <span className="ml-auto font-mono text-xs text-text-muted tabular-nums">
            {entries.length} {entries.length === 1 ? "event" : "events"}
          </span>
        </div>

        <ScrollArea className="min-h-0 isolate transform-gpu rounded-lg border border-border-subtle bg-surface-canvas">
          {newestFirst.length === 0 ? (
            <Empty className="h-full border-0">
              <EmptyHeader>
                <EmptyMedia variant="icon">
                  <TerminalIcon />
                </EmptyMedia>
                <EmptyTitle>No debug events yet</EmptyTitle>
                <EmptyDescription>
                  Video timing and console output will appear here.
                </EmptyDescription>
              </EmptyHeader>
            </Empty>
          ) : (
            <ol className="min-w-0">
              {newestFirst.map((entry) => (
                <DebugEntryRow key={entry.id} entry={entry} />
              ))}
            </ol>
          )}
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}
